import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import sortCaret from "../../components/SortCaret";
import AddEditDiscount from "./AddEditDiscount";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import DiscountActionButton from "./DiscountActionButton";
import Confirmation from "../../components/Confirmation";
import { useTrust } from "../../lib/context/TrustContext";
import UnauthorisedAccess from "../../UnauthorisedAccess";
import { useOrganization } from "../../lib/context/OrganisationContext";
import UploadDiscount from "./UploadDiscount";
import { InvoiceModuleConditon } from "../../lib/PermissionConditions";

const Discounts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationData } = useOrganization();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [discountList, setDiscountsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId } = useTrust();

  const [discountId, setDiscountId] = useState(null);
  const [showAddEditDiscount, setShowAddEditDiscount] = useState(false);
  const [showDiscountDelete, setShowDiscountDelete] = useState(false);

  const toggleShowAddEditDiscount = () => {
    if (showAddEditDiscount) {
      setEdit(false);
      setDiscountId(null);
    }
    setShowAddEditDiscount(!showAddEditDiscount);
  };

  const toggleShowDelete = () => {
    if (showDiscountDelete) {
      setDiscountId(null);
    }
    setShowDiscountDelete(!showDiscountDelete);
  };

  const fetchDiscounts = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_DISCOUNT_CODES}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const discountList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < discountList?.length; i++) {
            notSelectedIds.push(discountList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setDiscountsList(discountList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setDiscountsList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId]
  );

  const discountsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: discountList[i]?.id,
        code: discountList[i]?.code ? discountList[i]?.code : "",
        description: discountList[i]?.description
          ? discountList[i]?.description
          : "",
        discount: discountList[i]?.discount
          ? discountList[i]?.discount_type === 0
            ? `$${discountList[i]?.discount}`
            : `${discountList[i]?.discount}%`
          : "",
        action: (
          <>
            <DiscountActionButton
              id={discountList[i]?.id}
              setEdit={setEdit}
              setDiscountId={setDiscountId}
              toggleShowAddEditDiscount={toggleShowAddEditDiscount}
              toggleShowDelete={toggleShowDelete}
              used={discountList[i]?.is_used ? true : false}
            />
          </>
        ),
      });
    }
    return items;
  };

  const discounts = discountsGenerator(discountList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "code",
      text: "Code",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "discount",
      text: "Discount",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchDiscounts(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchDiscounts(limit, page);
  };

  // Single COA Delete
  const handleDiscountDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.DISCOUNT_CODE_DELETE}/${discountId}`);
      setLoading(false);
      fetchDiscounts(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  return InvoiceModuleConditon(organizationData) ? (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        mainTitle={"Discount Codes"}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            <UploadDiscount
              fetchData={() => fetchDiscounts(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setLoading}
            />

            {/* Add Discount */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Discount Code" : "Add Discount Code"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setDiscountId(null);
                setShowAddEditDiscount(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Discount</span>
                </div>
              }
              className={"sm:max-w-1/2"}
              open={showAddEditDiscount}
              buttonClassName={""}
              setOpen={setShowAddEditDiscount}
              write={true}
            >
              <AddEditDiscount
                edit={edit}
                toggleShowAddEditDiscount={toggleShowAddEditDiscount}
                id={discountId}
                trustId={trustId}
                fetchDiscountData={() => fetchDiscounts(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={discounts}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Discount Code"}
        open={showDiscountDelete}
        setOpen={setShowDiscountDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete discount?"}
        okAction={async () => {
          handleDiscountDelete();
          toggleShowDelete();
          // fetchDiscounts();
        }}
        cancelAction={() => {
          setDiscountId(null);
          setShowDiscountDelete(!showDiscountDelete);
        }}
      />
    </>
  ) : (
    <UnauthorisedAccess />
  );
};

export default Discounts;
